import { useEffect, useState, useRef } from "react";
import api, { formatErr } from "@/lib/api";
import { Upload } from "lucide-react";
import { toast } from "sonner";
import AuthedImage from "@/components/AuthedImage";

export default function SettingsPage() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    api.get("/profile").then((r) => setForm(r.data));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", "logos");
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo_path: data.storage_path }));
      toast.success("Logo uploaded");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile", form);
      setForm(data);
      toast.success("Settings saved");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  if (!form) return <div className="p-12 text-muted-foreground">Loading…</div>;

  return (
    <div className="px-6 lg:px-14 py-10 lg:py-14 max-w-5xl" data-testid="settings-page">
      <div className="overline">Studio</div>
      <h1 className="font-serif font-light mt-3" style={{ fontSize: "44px", lineHeight: 1.05 }}>Company Settings</h1>
      <p className="text-muted-foreground mt-3 mb-8 max-w-2xl">
        Configure how your studio appears in proposals, PDFs and client-facing documents.
      </p>

      {/* Section jump nav — Prompt 3B IA: five human-friendly groups
          + Advanced. Anchors scroll to the existing content blocks so
          no schema / route / API change is required. */}
      <nav className="border-y border-border py-3 mb-10 flex flex-wrap gap-x-6 gap-y-2 text-xs uppercase tracking-widest"
           data-testid="settings-nav">
        {[
          ["business", "Business"],
          ["pricing", "Pricing"],
          ["materials-suppliers", "Materials & Suppliers"],
          ["client-experience", "Client Experience"],
          ["sales-workflow", "Sales Workflow"],
          ["advanced", "Advanced"],
        ].map(([id, label]) => (
          <a key={id} href={`#settings-${id}`}
             data-testid={`settings-nav-${id}`}
             className="text-muted-foreground hover:text-foreground transition-colors">
            {label}
          </a>
        ))}
      </nav>

      <section id="settings-business" className="scroll-mt-6"></section>
      <div className="grid md:grid-cols-[260px,1fr] gap-10">
        <div>
          <div className="overline mb-3">Logo</div>
          <div className="w-full aspect-square bg-muted border border-border flex items-center justify-center overflow-hidden">
            {form.logo_path ? (
              <AuthedImage path={form.logo_path} alt="logo" className="w-full h-full object-cover"/>
            ) : (
              <div className="font-serif text-3xl text-[color:hsl(var(--accent))]">{form.company_name?.charAt(0) || "L"}</div>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}/>
          <button data-testid="btn-upload-logo" onClick={() => fileRef.current.click()} className="btn-outline w-full justify-center mt-3 text-xs">
            <Upload className="w-3 h-3" strokeWidth={1.5}/> Upload logo
          </button>
        </div>

        <div className="space-y-5">
          <Field label="Company name" testid="input-company-name" value={form.company_name || ""} onChange={set("company_name")}/>
          <Field label="Tagline" testid="input-tagline" value={form.tagline || ""} onChange={set("tagline")}/>
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Email" testid="input-company-email" value={form.email || ""} onChange={set("email")}/>
            <Field label="Phone" testid="input-company-phone" value={form.phone || ""} onChange={set("phone")}/>
          </div>
          <Field label="Address" testid="input-company-address" value={form.address || ""} onChange={set("address")}/>

          <div className="pt-4">
            <button data-testid="btn-save-settings" onClick={save} disabled={saving} className="btn-primary">
              {saving ? "Saving…" : "Save settings"}
            </button>
          </div>
        </div>
      </div>

      <div id="settings-pricing" className="scroll-mt-6"><ScoringSettings/></div>
      <div id="settings-client-experience" className="scroll-mt-6"><EmailSettingsSection/></div>
      <div id="settings-notifications-anchor" className="scroll-mt-6"><NotificationPreferencesSection/></div>
      <div id="settings-materials-suppliers" className="scroll-mt-6"><SupplierAssetLibrariesLink/></div>
      <div id="settings-sales-workflow" className="scroll-mt-6"></div>
    </div>
  );
}

function SupplierAssetLibrariesLink() {
  return (
    <div className="luxe-card p-6 mt-6" data-testid="supplier-libraries-link-card">
      <div className="flex items-center justify-between gap-6 flex-wrap">
        <div>
          <div className="overline">Supplier assets</div>
          <h2 className="font-serif font-light mt-2" style={{ fontSize: "24px" }}>
            Supplier Asset Libraries
          </h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-xl">
            Register and configure every supplier that provides Subtend Studio with digital assets —
            asset library ZIPs, hosted feeds or dedicated adapters. Enable auto-populate
            per supplier and see status at a glance.
          </p>
        </div>
        <a href="/settings/supplier-libraries"
           className="btn-outline text-xs"
           data-testid="link-manage-supplier-libraries">
          Manage suppliers
        </a>
      </div>

      {/* ── Advanced ─── Prompt 3 IA: house engineering/admin surfaces
          here so the primary Studio nav stays product-focused.
          Everything remains fully functional at its existing route
          — nothing was deleted. */}
      <div className="mt-8" id="settings-advanced" data-testid="settings-advanced">
        <div className="overline">Advanced</div>
        <h2 className="font-serif font-light mt-2" style={{ fontSize: "24px" }}>Advanced Studio Tools</h2>
        <p className="text-xs text-muted-foreground mt-1 max-w-xl mb-4">
          Pricing engineering, supplier administration, technical diagnostics.
          Most projects will not need these — they are here when you do.
        </p>
        <div className="grid sm:grid-cols-2 gap-3 max-w-2xl">
          <a href="/pricing" data-testid="link-advanced-pricing"
             className="luxe-card p-4 hover:shadow-md transition">
            <div className="overline text-[10px]">Pricing</div>
            <div className="font-medium mt-1">Trades, Rate Cards & Recipes</div>
            <div className="text-xs text-muted-foreground mt-1">
              Configure trade categories, rate cards, and reusable pricing recipes.
            </div>
          </a>
          <a href="/suppliers" data-testid="link-advanced-suppliers"
             className="luxe-card p-4 hover:shadow-md transition">
            <div className="overline text-[10px]">Suppliers</div>
            <div className="font-medium mt-1">Supplier CRM</div>
            <div className="text-xs text-muted-foreground mt-1">
              Contacts, purchase-order metadata and trade relationships.
            </div>
          </a>
          <a href="/materials/thumbnails" data-testid="link-advanced-thumbnails"
             className="luxe-card p-4 hover:shadow-md transition">
            <div className="overline text-[10px]">Materials</div>
            <div className="font-medium mt-1">Thumbnail Discovery</div>
            <div className="text-xs text-muted-foreground mt-1">
              Review auto-discovered supplier thumbnails and match them to materials.
            </div>
          </a>
          <a href="/materials/import" data-testid="link-advanced-import"
             className="luxe-card p-4 hover:shadow-md transition">
            <div className="overline text-[10px]">Materials</div>
            <div className="font-medium mt-1">ZIP Library Import</div>
            <div className="text-xs text-muted-foreground mt-1">
              Ingest supplier library ZIPs (Polytec, Laminex, adapter-driven).
            </div>
          </a>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, value, onChange }) {
  return (
    <div>
      <label className="overline block mb-2" style={{ color: "#5C5C5C" }}>{label}</label>
      <input data-testid={testid} className="luxe-input" value={value} onChange={onChange}/>
    </div>
  );
}

function ScoringSettings() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState({ budget_band: "$50k-$75k", suburb: "Noosa Heads", project_stage: "plans_underway", architect_involved: true, builder_involved: false, interior_designer_involved: false, uploads: [{ kind: "photo" }] });
  const [previewResult, setPreviewResult] = useState(null);

  useEffect(() => {
    api.get("/settings/scoring").then((r) => setCfg(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!cfg) return;
    api.post("/leads/score-preview", preview).then((r) => setPreviewResult(r.data)).catch(() => {});
  }, [preview, cfg]);

  if (!cfg) return null;
  const rules = cfg.config;

  const patchRule = (group, key, value) => {
    const next = { ...rules, [group]: { ...rules[group], [key]: Number(value) || 0 } };
    setCfg({ ...cfg, config: next });
  };
  const patchThreshold = (key, value) => {
    const next = { ...rules.thresholds, [key]: Number(value) || 0 };
    setCfg({ ...cfg, config: { ...rules, thresholds: next } });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { thresholds, ...rest } = rules;
      await api.put("/settings/scoring", { rules: rest, thresholds });
      toast.success("Scoring saved");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const groupTable = (title, group, keyLabel = "Value") => (
    <div className="luxe-card p-6">
      <div className="overline mb-3">{title}</div>
      <div className="space-y-2">
        {Object.entries(rules[group] || {}).map(([k, v]) => (
          <div key={k} className="flex items-center justify-between gap-4">
            <span className="text-sm text-muted-foreground truncate">{k.replace(/_/g, " ")}</span>
            <input type="number" step="1" value={v}
              onChange={(e) => patchRule(group, k, e.target.value)}
              data-testid={`scoring-${group}-${k}`}
              className="luxe-input w-24 text-right text-sm"/>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="mt-16" data-testid="scoring-settings">
      <div className="overline">Lead Scoring</div>
      <h2 className="font-serif font-light mt-2 mb-6" style={{ fontSize: "32px" }}>Scoring Configuration</h2>
      <p className="text-muted-foreground mb-8 max-w-2xl">
        Point values below feed the automatic lead scoring engine. Adjust freely — every change re-runs the preview on the right.
      </p>

      <div className="grid lg:grid-cols-[1fr,340px] gap-6">
        <div className="grid md:grid-cols-2 gap-6">
          {groupTable("Budget Bands", "budget_bands")}
          {groupTable("Priority Suburbs", "priority_suburbs")}
          {groupTable("Professionals", "professional")}
          {groupTable("Readiness", "readiness")}
          {groupTable("Uploads", "uploads")}
          {groupTable("Source Bonus", "source_bonus")}
          <div className="luxe-card p-6 md:col-span-2">
            <div className="overline mb-3">Classification Thresholds</div>
            <div className="grid grid-cols-3 gap-3">
              {["priority", "qualified", "nurture"].map((k) => (
                <div key={k}>
                  <label className="overline text-[10px] block mb-1">{k}</label>
                  <input type="number" value={rules.thresholds?.[k] ?? 0}
                    onChange={(e) => patchThreshold(k, e.target.value)}
                    data-testid={`scoring-threshold-${k}`}
                    className="luxe-input"/>
                </div>
              ))}
            </div>
          </div>
        </div>

        <aside className="lg:sticky lg:top-6 self-start">
          <div className="luxe-card p-6" data-testid="scoring-preview">
            <div className="overline mb-3">Test Score</div>
            <div className="space-y-3 text-sm">
              <div>
                <label className="overline text-[10px] block mb-1">Budget</label>
                <select className="luxe-input" value={preview.budget_band} onChange={(e) => setPreview({ ...preview, budget_band: e.target.value })}>
                  {Object.keys(rules.budget_bands || {}).map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <label className="overline text-[10px] block mb-1">Suburb</label>
                <input className="luxe-input" value={preview.suburb} onChange={(e) => setPreview({ ...preview, suburb: e.target.value })}/>
              </div>
              <div>
                <label className="overline text-[10px] block mb-1">Stage</label>
                <select className="luxe-input" value={preview.project_stage} onChange={(e) => setPreview({ ...preview, project_stage: e.target.value })}>
                  {Object.keys(rules.readiness || {}).map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={preview.architect_involved} onChange={(e) => setPreview({ ...preview, architect_involved: e.target.checked })}/> Architect involved</label>
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={preview.interior_designer_involved} onChange={(e) => setPreview({ ...preview, interior_designer_involved: e.target.checked })}/> Interior designer</label>
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={preview.builder_involved} onChange={(e) => setPreview({ ...preview, builder_involved: e.target.checked })}/> Builder</label>
            </div>
            <div className="border-t border-border mt-5 pt-5">
              <div className="font-serif font-light" style={{ fontSize: "48px", lineHeight: 1 }}>{previewResult?.score ?? "—"}</div>
              <div className="overline mt-2" style={{ color: "#A68A64" }}>{previewResult?.classification || "—"}</div>
              <div className="mt-3 space-y-1">
                {(previewResult?.reasons || []).map((r, i) => (
                  <div key={i} className="text-[11px] flex justify-between">
                    <span className="text-muted-foreground">{r.factor}</span>
                    <span className="font-mono">+{r.points}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </aside>
      </div>

      <div className="mt-8">
        <button data-testid="btn-save-scoring" onClick={save} disabled={saving} className="btn-primary">
          {saving ? "Saving…" : "Save scoring"}
        </button>
      </div>
    </div>
  );
}

// ── Email Settings ────────────────────────────────────────────────────
function EmailSettingsSection() {
  const [cfg, setCfg] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [events, setEvents] = useState([]);
  const [retrying, setRetrying] = useState(null);

  const load = () => api.get("/settings/email").then((r) => { setCfg(r.data); setDirty(false); });
  const loadEvents = () => api.get("/email-events?status=failed&limit=30").then((r) => setEvents(r.data)).catch(() => {});
  useEffect(() => { load(); loadEvents(); }, []);

  const setOverride = (k, v) => {
    setCfg((c) => ({ ...c, overrides: { ...c.overrides, [k]: v } }));
    setDirty(true);
  };
  const setTplOverride = (tid, field, v) => {
    setCfg((c) => {
      const t = { ...(c.template_overrides || {}) };
      t[tid] = { ...(t[tid] || {}) };
      if (v === "" || v == null) delete t[tid][field]; else t[tid][field] = v;
      if (Object.keys(t[tid]).length === 0) delete t[tid];
      return { ...c, template_overrides: t };
    });
    setDirty(true);
  };
  const resetTpl = (tid) => {
    setCfg((c) => {
      const t = { ...(c.template_overrides || {}) };
      delete t[tid];
      return { ...c, template_overrides: t };
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        sender_name: cfg.overrides.sender_name,
        from_address: cfg.overrides.from_address,
        reply_to: cfg.overrides.reply_to,
        signature_html: cfg.overrides.signature_html,
        template_overrides: cfg.template_overrides || {},
      };
      const { data } = await api.put("/settings/email", body);
      setCfg(data); setDirty(false);
      toast.success("Email settings saved");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const retry = async (id) => {
    setRetrying(id);
    try {
      const { data } = await api.post(`/email-events/${id}/retry`);
      toast.success(data.retry_event?.status === "sent" ? "Retry sent" : `Retry logged (${data.retry_event?.status})`);
      loadEvents();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setRetrying(null); }
  };

  if (!cfg) return null;
  const previewSubject = "Your Subtend Studio project is ready to view — 12 Ocean Drive";
  const senderLine = `${cfg.overrides.sender_name || cfg.defaults.sender_name} <${cfg.overrides.from_address || cfg.defaults.from_address}>`;
  // Show the warning immediately while typing an external from_address, in
  // addition to the after-save backend flag.
  const typedFrom = (cfg.overrides.from_address || cfg.defaults.from_address || "").toLowerCase();
  const domain = (cfg.verified_domain || "").toLowerCase();
  const typedFromExternal = typedFrom && domain && !typedFrom.endsWith("@" + domain);
  const fromWarning = typedFromExternal || !cfg.from_verified;

  return (
    <div className="mt-16" data-testid="email-settings">
      <div className="overline">Email</div>
      <h2 className="font-serif font-light mt-2 mb-3" style={{ fontSize: "32px" }}>Email Sending & Templates</h2>
      <p className="text-muted-foreground text-sm max-w-2xl">Manage how customer-facing emails appear. Anything left blank falls back to the environment default so nothing ever breaks.</p>

      <div className="mt-8 grid lg:grid-cols-[1fr,320px] gap-8">
        <div className="space-y-5">
          <Field label="Sender name" testid="input-sender-name"
                 value={cfg.overrides.sender_name || ""} onChange={(e) => setOverride("sender_name", e.target.value)}/>
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="From address" testid="input-from-address"
                   value={cfg.overrides.from_address || ""} onChange={(e) => setOverride("from_address", e.target.value)}
                   placeholder={cfg.defaults.from_address}/>
            <Field label="Reply-to" testid="input-reply-to"
                   value={cfg.overrides.reply_to || ""} onChange={(e) => setOverride("reply_to", e.target.value)}
                   placeholder={cfg.defaults.reply_to}/>
          </div>
          {fromWarning && (
            <div className="text-xs border-l-2 border-[color:hsl(var(--destructive))] pl-3 py-1" data-testid="from-domain-warning">
              Heads up: the From address is outside your verified sending domain ({cfg.verified_domain}). Resend may reject it or deliverability may suffer.
            </div>
          )}
          <div>
            <label className="overline block mb-2" style={{ color: "#5C5C5C" }}>Email signature (light HTML)</label>
            <textarea data-testid="input-signature" rows={4} className="luxe-input font-mono text-xs"
                      value={cfg.overrides.signature_html || ""}
                      onChange={(e) => setOverride("signature_html", e.target.value)}
                      placeholder="<p>Warmly,<br/>Petra · Studio Director</p>"/>
            <div className="text-[10px] text-muted-foreground mt-1">Allowed tags: b, strong, em, i, u, br, p, a, span. Everything else is stripped.</div>
          </div>
        </div>

        <aside className="luxe-card p-5" data-testid="email-preview">
          <div className="overline mb-2 text-[10px]">Preview — Portal invitation</div>
          <div className="text-[10px] text-muted-foreground">From</div>
          <div className="text-xs font-mono break-all">{senderLine}</div>
          <div className="text-[10px] text-muted-foreground mt-2">Reply-to</div>
          <div className="text-xs font-mono">{cfg.overrides.reply_to || cfg.defaults.reply_to}</div>
          <div className="text-[10px] text-muted-foreground mt-2">Subject</div>
          <div className="text-xs">{(cfg.template_overrides?.portal_invite?.subject || previewSubject).replace("{project_title}", "12 Ocean Drive")}</div>
          <div className="mt-3 text-[10px] text-muted-foreground">Signature</div>
          <div className="border border-border p-2 mt-1 text-xs" dangerouslySetInnerHTML={{ __html: cfg.overrides.signature_html || "<span class='text-muted-foreground'>None</span>" }}/>
        </aside>
      </div>

      {/* Templates */}
      <div className="mt-10">
        <div className="overline">Template overrides</div>
        <div className="mt-3 space-y-3">
          {(cfg.templates || []).map((t) => {
            const ov = (cfg.template_overrides || {})[t.id] || {};
            const has = Object.keys(ov).length > 0;
            return (
              <details key={t.id} className="luxe-card" data-testid={`tpl-${t.id}`} open={has}>
                <summary className="cursor-pointer px-5 py-3 flex items-center justify-between">
                  <div>
                    <div className="font-serif text-lg">{t.label}</div>
                    <div className="text-xs text-muted-foreground">{has ? "Customised" : "Using default"}</div>
                  </div>
                  {has && (
                    <button type="button" onClick={(e) => { e.preventDefault(); resetTpl(t.id); }}
                            data-testid={`btn-reset-tpl-${t.id}`} className="text-xs text-muted-foreground hover:text-foreground">Reset to default</button>
                  )}
                </summary>
                <div className="px-5 pb-5 pt-2 space-y-3">
                  <TplField label="Subject" placeholder={t.default_subject} testid={`tpl-${t.id}-subject`}
                            value={ov.subject || ""} onChange={(v) => setTplOverride(t.id, "subject", v)}/>
                  <TplField label="Hero heading" placeholder={t.default_hero} testid={`tpl-${t.id}-hero`}
                            value={ov.hero || ""} onChange={(v) => setTplOverride(t.id, "hero", v)}/>
                  <TplTextarea label="Intro copy" placeholder={t.default_intro} testid={`tpl-${t.id}-intro`}
                               value={ov.intro || ""} onChange={(v) => setTplOverride(t.id, "intro", v)}/>
                  <div className="text-[10px] text-muted-foreground">Merge fields available: {"{first_name}, {project_title}, {reference}, {proposal_number}, {concept_title}"} depending on template.</div>
                </div>
              </details>
            );
          })}
        </div>
      </div>

      <div className="mt-6 flex items-center gap-3">
        <button data-testid="btn-save-email" onClick={save} disabled={!dirty || saving} className="btn-primary">
          {saving ? "Saving…" : "Save email settings"}
        </button>
        {dirty && <button data-testid="btn-cancel-email" onClick={load} className="btn-outline">Discard changes</button>}
      </div>

      {/* Failed email events */}
      <div className="mt-14" data-testid="failed-emails">
        <div className="overline">Failed deliveries</div>
        <h3 className="font-serif font-light mt-1" style={{ fontSize: "24px" }}>Retry failed emails</h3>
        {events.length === 0 ? (
          <div className="text-sm text-muted-foreground mt-3">No failed emails right now.</div>
        ) : (
          <div className="luxe-card divide-y divide-border mt-3">
            {events.map((ev) => (
              <div key={ev.id} className="p-4 flex items-start gap-4" data-testid={`failed-${ev.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{ev.template} → {ev.recipient}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{ev.subject || "—"}</div>
                  {ev.error && <div className="text-xs mt-1" style={{ color: "#B85C38" }}>{ev.error.slice(0, 200)}</div>}
                  <div className="text-[10px] text-muted-foreground mt-1">
                    {new Date(ev.created_at).toLocaleString("en-AU")}
                    {ev.retry_count > 0 && <> · retried {ev.retry_count}×</>}
                    {ev.link_audit && <> · link: <span className="font-mono">{ev.link_audit.masked_url}</span></>}
                  </div>
                </div>
                <button data-testid={`btn-retry-${ev.id}`}
                        disabled={retrying === ev.id}
                        onClick={() => retry(ev.id)}
                        className="btn-outline text-xs">
                  {retrying === ev.id ? "Retrying…" : "Retry"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TplField({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="overline text-[10px] block mb-1">{label}</label>
      <input data-testid={testid} className="luxe-input text-sm" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}/>
    </div>
  );
}
function TplTextarea({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="overline text-[10px] block mb-1">{label}</label>
      <textarea data-testid={testid} rows={3} className="luxe-input text-sm" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}/>
    </div>
  );
}

// ── Notification Preferences ─────────────────────────────────────────
function NotificationPreferencesSection() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings/notifications").then((r) => setData(r.data)).catch(() => {}); }, []);

  const toggle = (key, field) => {
    setData((d) => ({ ...d, prefs: { ...d.prefs, [key]: { ...d.prefs[key], [field]: !d.prefs[key][field] } } }));
  };
  const save = async () => {
    setSaving(true);
    try {
      const { data: fresh } = await api.put("/settings/notifications", { prefs: data.prefs });
      setData(fresh);
      toast.success("Notification preferences saved");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  if (!data) return null;
  return (
    <div className="mt-16" data-testid="notification-preferences">
      <div className="overline">Notifications</div>
      <h2 className="font-serif font-light mt-2 mb-3" style={{ fontSize: "32px" }}>Notification preferences</h2>
      <p className="text-muted-foreground text-sm max-w-2xl">
        Choose which events show up in your Notification Centre and which also send you an email. Existing important events remain on by default.
      </p>
      <div className="mt-8 luxe-card">
        <table className="w-full text-sm">
          <thead className="bg-[color:hsl(var(--surface-panel))]">
            <tr>
              <th className="text-left p-3">Event</th>
              <th className="text-center p-3 w-24">In-app</th>
              <th className="text-center p-3 w-24">Email</th>
            </tr>
          </thead>
          <tbody>
            {data.events.map((ev) => {
              const p = data.prefs[ev.key];
              return (
                <tr key={ev.key} className="border-t border-border" data-testid={`pref-row-${ev.key}`}>
                  <td className="p-3">
                    <div className="font-medium">{ev.label}</div>
                    <div className="overline text-[10px] mt-0.5">{ev.category}</div>
                  </td>
                  <td className="text-center p-3">
                    <input type="checkbox" data-testid={`pref-inapp-${ev.key}`}
                           checked={p.in_app} onChange={() => toggle(ev.key, "in_app")}/>
                  </td>
                  <td className="text-center p-3">
                    <input type="checkbox" data-testid={`pref-email-${ev.key}`}
                           checked={p.email} onChange={() => toggle(ev.key, "email")}/>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-6">
        <button data-testid="btn-save-notif-prefs" onClick={save} disabled={saving} className="btn-primary">
          {saving ? "Saving…" : "Save preferences"}
        </button>
      </div>
    </div>
  );
}

